import React, { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
    Checkbox, FormControlLabel, Grid2, Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import FormHelperText from "@mui/material/FormHelperText";
import { useGetPaymentPlans } from "../../../../../hooks/useGetPaymentPlans";
import axiosManager from "../../../../../services/axiosManager";
import { useTranslate } from "../../../../../hooks/useTranslate";
import { useToast } from "../../../../../context/toast";
import Preloader from "../../../../../components/Preloader";
import { formatCurrency, formatPrice } from "../../../../../utils/formatCurrency";
import TextInput from "../../../../../components/TextInput";
import "./ReEnrolContracts.css";

const currency = "GBP";

const ReEnrolContracts = (props) => {
    const {
        activeStudentId, onFinish,
    } = props;
    const { t } = useTranslate();
    const toast = useToast();

    const {
        isLoading, paymentPlans, refetch, isRefetching,
    } = useGetPaymentPlans(activeStudentId);

    const [discountCode, setDiscountCode] = useState("");
    const [termsChecked, setTermsChecked] = useState(false);
    const [showTermsError, setShowTermsError] = useState(false);

    const discounts = useMemo(
        () => (paymentPlans || [])
            .map((item) => item?.discounts)
            .flat()
            .filter((d, index, arr) => arr.findIndex((x) => x?.code === d?.code) === index),
        [paymentPlans],
    );

    const {
        mutateAsync: applyCode, isPending: isApplyingCode,
    } = useMutation({
        mutationFn: () => axiosManager.post$(`/parent/api/students/${activeStudentId}/PromotionCodes/${discountCode}`, {}).then((response) => response?.data),
        onError: (err) => toast.error(err.status === 400 ? err.response?.data ?? t("common.errorMessage") : t("common.errorMessage")),
    });

    const {
        mutateAsync: removeCode, isPending: isRemovingCode,
    } = useMutation({
        mutationFn: (discount) => axiosManager.delete$(`/parent/api/students/${activeStudentId}/PromotionCodes/${discount}`).then((response) => response?.data),
        onError: () => toast.error(t("common.errorMessage")),
    });

    const {
        mutateAsync: confirmReEnrol, isPending: isConfirmingReEnrol,
    } = useMutation({
        mutationFn: () => axiosManager.post$(`/parent/api/students/${activeStudentId}/reenrolment/confirm`, {}).then((response) => response?.data),
        onError: () => {
            toast.error(t("common.errorMessage"));
        },
    });

    const onApplyCode = async () => {
        try {
            await applyCode({ discountCode });
            refetch();
        } catch {
            // Error is in Mutation
        }
    };
    const onRemoveCode = async (code) => {
        try {
            await removeCode(code);
            refetch();
        } catch {
            // Error is in Mutation
        }
    };
    const onConfirm = async () => {
        if (!termsChecked) {
            setShowTermsError(true);
            return;
        }

        try {
            await confirmReEnrol();
            onFinish();
        } catch {
            // Error in Mutatiom
        }
    };

    return (
        <Preloader isLoading={isLoading}>
            <Preloader isLoading={isRefetching} overflow showInitiallyOverflow>
                <div className="rejoin-contracts">
                    <div className="rejoin-package-selection__box rejoin-package-selection__box--edit">
                        <Typography variant="h4" component="div" className="text-primary">
                            {t("reEnrol.contractsTitle")}
                        </Typography>
                        <Typography variant="body1" component="div" className="text-primary">
                            <p>{t("reEnrol.contractsSub2")}</p>
                        </Typography>
                        <Grid2 container spacing={2} className="w-full mt-6" alignItems="stretch">
                            {(paymentPlans || []).map((contract) => (
                                <Grid2 size={{ xs: 12, md: 6 }} key={contract.productId} className="mb-4">
                                    <div className="contract-box">
                                        {contract.type === "Full" && (
                                            <div className="contract-box__recommended">
                                                {t("reEnrol.recommended")}
                                            </div>
                                        )}
                                        <div className="contract-box__title">
                                            {t(`reEnrol.contracts.${contract.type}.title`)}
                                        </div>
                                        {contract.oneOffAmount > 0 && (
                                            <div className="contract-box__primary-text">
                                                <b>
                                                    {formatCurrency(currency).format(contract.oneOffAmount)}
                                                    {contract.instalments.length > 0 ? t("tuitionBilling.month") : ""}
                                                </b>
                                                {t(contract.instalments.length > 0 ? "tuitionBilling.forMonth" : "tuitionBilling.oneOffPayment", { num: contract.instalments.length })}
                                                {`${(contract.instalments.length > 12 && contract.deposit !== 0) ? ` + ${t("tuitionBilling.refundableDepositDue")} ` : ""}`}
                                            </div>
                                        )}
                                        {(contract.instalments.length > 12 && contract.deposit !== 0) && (
                                            <div className="contract-box__primary-text">
                                                <b>{formatCurrency(currency).format(contract.deposit)}</b>
                                                {t("tuitionBilling.refundableDepositDueText")}
                                            </div>
                                        )}
                                        <div className="mt-2">
                                            {t(`reEnrol.contracts.${contract.type}.description`)}
                                        </div>
                                    </div>
                                </Grid2>
                            ))}
                            <Typography variant="body1" component="div" className="text-primary">
                                <b>{t("reEnrol.feesTitle")}</b>
                                <br />
                                {t("reEnrol.feesText")}
                            </Typography>
                        </Grid2>
                    </div>
                    <div className="rejoin-package-selection__box">
                        <Typography variant="h5" component="div" className="text-primary">
                            {t("reEnrol.discountCode")}
                        </Typography>
                        <div className="rejoin-contracts__apply-code mt-4">
                            <div className="flex align-end gap-4">
                                <TextInput
                                    placeholder={t("reEnrol.discountCodePlaceholder")}
                                    name="discountCode"
                                    value={discountCode}
                                    disabled={isApplyingCode || isRemovingCode || isConfirmingReEnrol}
                                    onChange={(e) => setDiscountCode(e.target.value)}
                                />
                                <Button
                                    className="button-primary"
                                    loading={isApplyingCode || isConfirmingReEnrol}
                                    onClick={onApplyCode}
                                >
                                    {t("reEnrol.applyCode")}
                                </Button>
                            </div>
                            {discounts?.length > 0 && (
                                <div className="rejoin-contracts__applied mt-4">
                                    <Typography variant="body1" component="div" className="text-primary">
                                        {t("reEnrol.discountCodeTitle")}
                                    </Typography>
                                    {discounts.map((discount) => (
                                        <div key={discount.id} className="rejoin-contracts__applied__code">
                                            {`${discount.code}`}
                                            {discount?.promoCode ? (
                                                <IconButton
                                                    aria-label="close"
                                                    onClick={() => onRemoveCode(discount?.promoCode?.code || discount.code)}
                                                    disableRipple
                                                    disabled={isRemovingCode}
                                                >
                                                    <CloseIcon />
                                                </IconButton>
                                            ) : null}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="rejoin-contracts-terms">
                        <div>
                            <Typography variant="h4" component="div" className="text-primary">
                                {t("reEnrol.terms&Conditions")}
                            </Typography>
                            <FormControlLabel
                                className="rejoin-contracts-terms__checkbox"
                                disabled={isConfirmingReEnrol}
                                control={(
                                    <Checkbox
                                        checked={termsChecked}
                                        onChange={(e) => setTermsChecked(!termsChecked)}
                                    />
                                )}
                                label={(
                                    <>
                                        {t("reEnrol.iAgreeTo")}
                                        <a
                                            href="https://kingsinterhigh.co.uk/terms-of-use/"
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            {t("reEnrol.termsAndConditions")}
                                        </a>
                                    </>
                                )}
                            />
                            {showTermsError && !termsChecked
                                ? <FormHelperText className="text-error">{t("formErrors.required")}</FormHelperText> : null}
                        </div>
                        <Button
                            className="button-accent rejoin-contracts-terms__button"
                            loading={isConfirmingReEnrol}
                            onClick={onConfirm}
                        >
                            {t("reEnrol.confirmReEnrolment")}
                        </Button>
                    </div>
                </div>
            </Preloader>
        </Preloader>
    );
};

export default ReEnrolContracts;
